// src/components/controlPanel/CalendarOverrides.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import CalendarGrid from "./CalendarGrid";
import OverrideEditModal from "./OverrideEditModal";
import { fetchOverrides, createOverrides, deleteOverrides } from "@services/openingHoursOverrides";

// Format date as YYYY-MM-DD using local timezone (not UTC)
const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function CalendarOverrides({ defaultOpeningHours, onOverridesChange }) {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Date range picker state
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [includeDays, setIncludeDays] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  });

  // Override edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date();
      const fromDate = formatDateLocal(today);
      const toDate = new Date(today);
      toDate.setFullYear(toDate.getFullYear() + 1);
      const toDateStr = formatDateLocal(toDate);

      const response = await fetchOverrides(fromDate, toDateStr);
      const data = response?.data || response;
      setOverrides(data.overrides || []);
    } catch (err) {
      setError(err.message || "Failed to load overrides");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  const handleSelectDate = (date) => {
    const dateStr = formatDateLocal(date);
    setSelectedDates([dateStr]);
    setEditModalOpen(true);
  };

  const handleSelectRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(formatDateLocal(current));
      current.setDate(current.getDate() + 1);
    }

    setSelectedDates(dates);
    setEditModalOpen(true);
  };

  const getDefaultHoursForDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[d.getDay()];
    return defaultOpeningHours?.[dayName] || null;
  };

  const getExistingOverride = (dateStr) => {
    return overrides.find((o) => o.date === dateStr) || null;
  };

  const handleSaveOverride = async (payload) => {
    setSaving(true);
    try {
      await createOverrides(payload);
      await loadOverrides();
      setEditModalOpen(false);
      setSelectedDates([]);
      if (onOverridesChange) onOverridesChange();
    } catch (err) {
      setError(err.message || "Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async (dates) => {
    setSaving(true);
    try {
      await deleteOverrides(dates);
      await loadOverrides();
      setEditModalOpen(false);
      setSelectedDates([]);
      if (onOverridesChange) onOverridesChange();
    } catch (err) {
      setError(err.message || "Failed to delete override");
    } finally {
      setSaving(false);
    }
  };

  const resetRangeForm = () => {
    setRangeFrom("");
    setRangeTo("");
    setIncludeDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    });
  };

  const toggleDay = (day) => {
    setIncludeDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  const todayStr = useMemo(() => formatDateLocal(new Date()), []);
  const maxDateStr = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return formatDateLocal(d);
  }, []);

  const handleApplyRange = () => {
    if (!rangeFrom || !rangeTo) return;
    if (rangeFrom > rangeTo) return;

    const dates = [];
    const current = new Date(rangeFrom + "T00:00:00");
    const end = new Date(rangeTo + "T00:00:00");

    while (current <= end) {
      const dayName = dayNames[current.getDay()];
      if (includeDays[dayName]) {
        dates.push(formatDateLocal(current));
      }
      current.setDate(current.getDate() + 1);
    }

    if (dates.length === 0) {
      setError("No dates match your selection. Check the day filters.");
      return;
    }

    setSelectedDates(dates);
    setEditModalOpen(true);
    resetRangeForm();
  };

  const anyDaySelected = Object.values(includeDays).some((v) => v);
  const rangeValid = rangeFrom && rangeTo && rangeFrom <= rangeTo && anyDaySelected;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="flex items-center gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Loading calendar...</span>
        </div>
      </div>
    );
  }

  if (error && overrides.length === 0) {
    return (
      <div className="text-sm text-red-600 py-4 text-center">
        {error}
        <button
          onClick={loadOverrides}
          className="ml-2 text-teal-600 hover:text-teal-700 font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Date Range Picker */}
      <div className="mb-5 p-3 sm:p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-3">Select date range</p>

        {/* From/To inputs - stack on mobile */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={rangeFrom}
              onChange={(e) => setRangeFrom(e.target.value)}
              min={todayStr}
              max={maxDateStr}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
              min={rangeFrom || todayStr}
              max={maxDateStr}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Day of week filters - grid on mobile */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-2">Include days</label>
          <div className="grid grid-cols-4 sm:flex gap-2">
            {[
              { key: "monday", label: "Mon" },
              { key: "tuesday", label: "Tue" },
              { key: "wednesday", label: "Wed" },
              { key: "thursday", label: "Thu" },
              { key: "friday", label: "Fri" },
              { key: "saturday", label: "Sat" },
              { key: "sunday", label: "Sun" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleDay(key)}
                className={`px-2 sm:px-3 py-2 text-xs font-medium rounded-lg border transition ${
                  includeDays[key]
                    ? "bg-teal-100 border-teal-300 text-teal-700"
                    : "bg-white border-gray-200 text-gray-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleApplyRange}
          disabled={!rangeValid}
          className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition ${
            rangeValid
              ? "bg-teal-600 text-white hover:bg-teal-700"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Apply to Selected Days
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="text-sm text-red-600 py-2 mb-4 text-center">
          {error}
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 border-t border-gray-200" />
        <span className="text-xs text-gray-400">or click a day below</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Calendar Grid */}
      <div>
        <CalendarGrid
          defaultOpeningHours={defaultOpeningHours}
          overrides={overrides}
          selectedDates={selectedDates}
          onSelectDate={handleSelectDate}
          onSelectRange={handleSelectRange}
        />
      </div>

      {/* Override Edit Modal */}
      <OverrideEditModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDates([]);
        }}
        selectedDates={selectedDates}
        defaultHoursForFirstDate={selectedDates.length > 0 ? getDefaultHoursForDate(selectedDates[0]) : null}
        existingOverride={selectedDates.length === 1 ? getExistingOverride(selectedDates[0]) : null}
        onSave={handleSaveOverride}
        onDelete={handleDeleteOverride}
        saving={saving}
      />
    </>
  );
}
